import { useEffect, useState } from "react";

import api from "../../services/api";

// Qual documento a plataforma pede — e como o campo deve se comportar.
//
// Cobrando em real é CPF/CNPJ, com 11 ou 14 dígitos; cobrando em euro é NIF,
// com 9. Fixar um dos dois na tela fazia o formulário recusar o documento que
// ele mesmo pedia.
//
// A rota é pública porque a tela de cadastro precisa disso antes de existir
// login. Enquanto a resposta não chega vale o padrão brasileiro: é o que a
// maior parte das instalações usa, e trocar depois só reescreve um rótulo.

const PADRAO = {
  pais: "BR",
  nome: "Brasil",
  ddi: "55",
  rotuloDocumento: "CPF / CNPJ",
  descricaoDocumento:
    "CPF é o documento da pessoa (11 dígitos); CNPJ é o da empresa (14). Usados no Brasil.",
  tamanhosDocumento: [11, 14],
  exemploDocumento: "000.000.000-00",
  tamanhoCep: 8,
  rotuloCep: "CEP",
  // Catálogo dos países que o servidor aceita. Vazio até a resposta chegar: a
  // chave de cliente estrangeiro só tem o que oferecer depois disso.
  opcoes: [],
};

// Uma chamada por sessão: vale para todas as telas e só muda quando o operador
// mexe na cobrança.
let cache = null;
let pedido = null;

const buscar = () => {
  if (cache) return Promise.resolve(cache);

  if (!pedido) {
    pedido = api
      .get("/settings/fiscal")
      .then(({ data }) => {
        cache = { ...PADRAO, ...(data || {}) };
        return cache;
      })
      .catch(() => PADRAO)
      .finally(() => {
        pedido = null;
      });
  }

  return pedido;
};

const usePaisFiscal = () => {
  const [regras, setRegras] = useState(cache || PADRAO);

  useEffect(() => {
    let vivo = true;

    buscar().then((valor) => {
      if (vivo) setRegras(valor);
    });

    return () => {
      vivo = false;
    };
  }, []);

  return regras;
};

// As regras em vigor num formulário: as do país escolhido à mão para um
// cliente estrangeiro, ou as da plataforma quando ninguém escolheu.
export const regrasVigentes = (plataforma, pais) => {
  if (!pais) return plataforma;

  const doPais = (plataforma?.opcoes || []).find((o) => o.pais === pais);

  return doPais ? { ...plataforma, ...doPais } : plataforma;
};

// Só dígitos, do jeito que o backend guarda e o gateway espera.
export const somenteDigitos = (valor) => String(valor || "").replace(/\D/g, "");

export const documentoCompleto = (valor, regras) => {
  const digitos = somenteDigitos(valor);
  const tamanhos = regras?.tamanhosDocumento || [];

  if (!tamanhos.length) return digitos.length >= 5 && digitos.length <= 20;

  return tamanhos.includes(digitos.length);
};

export const cepCompleto = (valor, regras) => {
  const digitos = somenteDigitos(valor);

  if (!regras?.tamanhoCep) return digitos.length >= 3;

  return digitos.length === regras.tamanhoCep;
};

// A máscara segue o país: o ponto-e-traço do CPF em cima de um NIF só atrapalha
// a leitura. Onde não há forma conhecida, agrupa de três em três.
export const mascararDocumento = (valor, regras) => {
  const maior = Math.max(...(regras?.tamanhosDocumento || [14]), 0) || 14;
  const d = somenteDigitos(valor).slice(0, maior);

  if (regras?.pais === "BR") {
    if (d.length <= 11) {
      return d
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
    }

    return d
      .replace(/(\d{2})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1/$2")
      .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
  }

  return d.replace(/(\d{3})(?=\d)/g, "$1 ").trim();
};

export const mascararCep = (valor, regras) => {
  const d = somenteDigitos(valor).slice(0, regras?.tamanhoCep || 12);

  if (regras?.pais === "BR") return d.replace(/(\d{5})(\d)/, "$1-$2");
  if (regras?.pais === "PT") return d.replace(/(\d{4})(\d)/, "$1-$2");

  return d;
};

export default usePaisFiscal;
